import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Award, Users, Clock } from "lucide-react";
import DenisWithDog from "../assets/images/DenisWithDog.jpeg";

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              About Avoca Veterinary
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Compassionate care for your beloved pets, delivered with dignity
              and respect in the comfort of your own home.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto">
          <div className="items-center text-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                My Story
              </h2>
              <div className="space-y-6 text-gray-600">
                <p>
                  I have been a companion and farm animal vet for the past 8
                  years. Having lost my own loved pets over the years has given
                  me an appreciation of how difficult the process can be.
                </p>
                <p>
                  Therefore my focus is on providing a calm, respectful
                  environment to help your companion pass with dignity.
                </p>
                <p>
                  If you are unsure whether or not it is the right time to let
                  your pet go, then I can make a home visit to assess his/her
                  quality of life and discuss possible treatment alternatives,
                  this may require me to study your pets previous medical
                  history.
                </p>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            {/* <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl p-8 text-center">
                <img
                  src={DenisWithDog}
                  alt="Denis with a patient"
                  className="w-72 h-72 rounded-full mx-auto mb-6 object-cover border-4 border-white shadow-lg"
                />
                <p className="text-primary-700 font-medium">
                  Denis with a patient
                </p>
              </div>
            </motion.div> */}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide our care and service to you and your
              pets.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Compassion",
                description:
                  "We understand the deep bond between pets and their families, and approach every situation with empathy and care.",
              },
              {
                icon: Award,
                title: "Excellence",
                description:
                  "With 8+ years of veterinary experience, we maintain the highest standards of professional care and medical expertise.",
              },
              {
                icon: Users,
                title: "Family-Centered",
                description:
                  "We believe in involving families in the decision-making process and providing support throughout the entire journey.",
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Experience & Dedication
              </h2>
              <div className="space-y-6">
                {[
                  {
                    icon: Clock,
                    number: "8+",
                    label: "Years of Veterinary Experience",
                  },
                  { icon: Users, number: "500+", label: "Families Helped" },
                  { icon: Heart, number: "100%", label: "Home Visit Service" },
                  {
                    icon: Award,
                    number: "24/7",
                    label: "Emergency Availability",
                  },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-center space-x-4"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <stat.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-primary-600">
                        {stat.number}
                      </div>
                      <div className="text-gray-600">{stat.label}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Service Area */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="card"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Service Area
              </h3>
              <p className="text-gray-600 mb-6">
                Based in East Yorkshire, I aim to cover most of Yorkshire and
                North Lincolnshire, providing compassionate care to families
                across the region.
              </p>
              <div className="bg-primary-50 rounded-lg p-4">
                <h4 className="font-semibold text-primary-800 mb-2">
                  Coverage Areas:
                </h4>
                <ul className="text-primary-700 space-y-1">
                  <li>• East Yorkshire</li>
                  <li>• North Yorkshire</li>
                  <li>• West Yorkshire</li>
                  <li>• South Yorkshire</li>
                  <li>• North Lincolnshire</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need Support or Guidance?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              I'm here to provide compassionate support and guidance during this
              difficult time. Every consultation is confidential and
              understanding.
            </p>
            <div className="flex justify-center">
              <a
                href="/contact#contact-form"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Contact Me
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
